import { useState, useEffect, useCallback, FunctionComponent } from 'react';
import styles from './EvaluationComponent.module.scss';
import { useDispatch, useSelector } from 'react-redux';

// V3 Components
import Button from 'components/UI/V3/Button/Button';
import AlertMessage from 'components/UI/V3/AlertMessage/AlertMessage';
import Spinner from 'components/UI/V3/Spinner/Spinner';

// Evaluation Components
import EvaluationHeader from './EvaluationHeader';
import ServiceCategorySelector from './ServiceCategorySelector';
import EvaluationBasicInfo from './EvaluationBasicInfo';
import EvaluationToggles from './EvaluationToggles';
import EvaluationPricing from './EvaluationPricing';
import EvaluationTaxableService from './EvaluationTaxableService';
import EvaluationStaffSelector from './EvaluationStaffSelector';
import EvaluationColorSelector from './EvaluationColorSelector';
import EvaluationQuestions from './EvaluationQuestions';

// Models and Actions
import {
    Service,
    ServiceDuration,
    Evaluation,
    EvaluationExpiry,
    createEmptyEvaluationService,
    EvaluationQuestion
} from 'model/Service';
import { saveServiceThunk, fetchEvaluationServiceThunk } from 'actions/services/ServicesActions';
import { RootState } from 'store';
import { ServicesStatus } from 'reducers/services/ServicesState';

// Hooks
import { useApiClientWrapper, useMarketplace, useMasterData } from 'hooks';
import { changeEvaluationStatusThunk, MarketplaceStatus } from '@spike/marketplace-action';
import useNonInitialEffect from '@versiondos/hooks';

// UI Components
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FieldError } from '@spike/model';
import { validateEvaluation, validateRequiredFields } from './Validations';
import { showSuccess } from '@spike/notifications-action';

export const EvaluationComponent: FunctionComponent = () => {
    const dispatch = useDispatch();
    const marketplace = useMarketplace();
    const masterData = useMasterData();
    const apiClientWrapper = useApiClientWrapper();

    const [errors, setErrors] = useState<Array<FieldError>>([]);
    const [evaluationQuestions, setEvaluationQuestions] = useState<Array<EvaluationQuestion>>([]);
    const [isEnabled, setIsEnabled] = useState(marketplace.evaluationEnabled);
    const [touchedQuestions, setTouchedQuestions] = useState<Set<number | string>>(new Set());
    const [attemptedSave, setAttemptedSave] = useState(false);
    const [deletedQuestionIds, setDeletedQuestionIds] = useState<number[]>([]);

    const status = useSelector<RootState, ServicesStatus>(state => state.services.status);
    const loading = useSelector<RootState, boolean>(state => state.services.loading);

    const marketplaceStatus = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);
    const evaluationService = useSelector<RootState, Service | null | undefined>(
        state => state.services.evaluationService
    );

    // Fetch evaluation service only once when component mounts
    const fetchEvaluationData = useCallback(() => {
        dispatch(fetchEvaluationServiceThunk());
    }, []);

    useEffect(() => {
        fetchEvaluationData();
    }, [fetchEvaluationData]);

    const [localService, setLocalService] = useState<Service>(
        evaluationService ? evaluationService : createEmptyEvaluationService(masterData, marketplace)
    );

    const [hasLoaded, setHasLoaded] = useState(true);

    const defaultQuestions = masterData.defaultEvaluationQuestions.map((q, index) => ({
        id: q.id,
        category: q.category ?? 'for_pets',
        displayOrder: q.displayOrder,
        enabled: q.enabled ?? true,
        questionText: q.questionText,
        questionableId: undefined,
        required: q.required ?? true,
        questionableType: 'Evaluation'
    }));

    // Handle data loading when evaluation service is fetched
    useEffect(() => {
        console.log('🔍 Status changed:', status);
        console.log('🔍 EvaluationService:', evaluationService);

        if (status === ServicesStatus.FetchEvaluationSuccess) {
            console.log('✅ Evaluation service fetch success');

            if (evaluationService) {
                // Load existing evaluation service
                console.log('📦 Loading existing evaluation service');
                console.log('📦 Service data:', {
                    name: evaluationService.name,
                    description: evaluationService.description,
                    categories: evaluationService.evaluation?.category,
                    pricing: evaluationService.pricing,
                    duration: evaluationService.duration
                });

                setLocalService(evaluationService);

                const questions =
                    (evaluationService.evaluation?.evaluationQuestions?.length || 0) > 0
                        ? evaluationService.evaluation?.evaluationQuestions || []
                        : defaultQuestions;

                console.log('❓ Questions loaded:', questions);
                setEvaluationQuestions(questions);
            } else {
                // No evaluation service exists, create new one
                const newService = createEmptyEvaluationService(masterData, marketplace);
                setLocalService({ ...newService, duration: { simple: { hours: 0, minutes: 15 }, variable: [] } });
                setEvaluationQuestions(defaultQuestions);
            }

            // Reset UI states
            setTouchedQuestions(new Set());
            setAttemptedSave(false);
            setHasLoaded(false);
            setErrors([]);
        }

        if (status === ServicesStatus.SaveSuccess) {
            setAttemptedSave(false);
            setHasLoaded(false);
            dispatch(showSuccess('Evaluation service saved successfully'));
            dispatch(fetchEvaluationServiceThunk());
        }

        if (status === ServicesStatus.Error) {
            setHasLoaded(false);
            console.error('❌ Error with evaluation service operation');
        }
    }, [status]);

    useNonInitialEffect(() => {
        if (marketplaceStatus === MarketplaceStatus.Error) {
            setIsEnabled(false);
        }
    }, [marketplaceStatus]);

    const updateServiceEvaluation = (updates: Partial<Evaluation>) => {
        setLocalService(prev => ({
            ...prev,
            evaluation: prev.evaluation
                ? {
                      ...prev.evaluation,
                      ...updates
                  }
                : {
                      category: [],
                      evaluationExpiry: {
                          enabled: false,
                          duration: '4'
                      },
                      evaluationQuestionsEnabled: false,
                      ...updates
                  }
        }));
    };

    const updateEvaluationExpiry = (updates: Partial<EvaluationExpiry>) => {
        updateServiceEvaluation({
            evaluationExpiry: localService.evaluation
                ? {
                      ...localService.evaluation.evaluationExpiry,
                      ...updates
                  }
                : {
                      enabled: false,
                      duration: '',
                      ...updates
                  }
        });
    };

    const changeEnableHandler = (checked: boolean) => {
        setIsEnabled(checked);

        if (!checked) {
            setLocalService(prev => ({
                ...prev,
                evaluation: undefined
            }));

            dispatch(changeEvaluationStatusThunk(apiClientWrapper, false));
        }
    };

    const handleSave = async () => {
        setAttemptedSave(true);
        try {
            const payload: Service = isEnabled ? localService : { ...localService, evaluation: undefined };

            const validationErrors = validateEvaluation(payload);
            setErrors(validationErrors);

            if (validationErrors.length === 0) {
                if (marketplace.evaluationEnabled !== isEnabled) {
                    dispatch(changeEvaluationStatusThunk(apiClientWrapper, isEnabled));
                }

                dispatch(saveServiceThunk(payload, undefined, undefined, deletedQuestionIds));
            }
        } catch (error) {
            console.error('Error saving service with evaluation:', error);
        }
    };

    // Custom Questions handlers
    const handleToggleCustomQuestions = (enabled: boolean) => {
        if (enabled) {
            setEvaluationQuestions(defaultQuestions);
            updateServiceEvaluation({
                evaluationQuestionsEnabled: true,
                evaluationQuestions: defaultQuestions
            });
        } else {
            const idsToDelete = evaluationQuestions
                .map(q => (typeof q.id === 'number' ? (q.id as number) : undefined))
                .filter((id): id is number => id !== undefined);
            if (idsToDelete.length > 0) {
                setDeletedQuestionIds(prev => Array.from(new Set([...(prev || []), ...idsToDelete])));
            }
            updateServiceEvaluation({
                evaluationQuestionsEnabled: enabled,
                evaluationQuestions: evaluationQuestions
            });
        }
    };

    const handleUpdateQuestion = (questionId: number | string, field: keyof EvaluationQuestion, value: any) => {
        // Mark question as touched when user interacts with it
        if (field === 'questionText') {
            setTouchedQuestions(prev => new Set(prev).add(questionId));
        }

        const currentQuestions = evaluationQuestions;
        const updatedQuestions = currentQuestions.map((q, index) => {
            const qId = q.id || q.displayOrder || index;
            return qId === questionId ? { ...q, [field]: value } : q;
        });

        // Update both local state and service
        setEvaluationQuestions(updatedQuestions);
        updateServiceEvaluation({
            evaluationQuestions: updatedQuestions
        });
    };

    const handleRemoveQuestion = (questionId: number | string) => {
        const currentQuestions = evaluationQuestions;
        const updatedQuestions = currentQuestions.filter((q, index) => {
            const qId = q.id || q.displayOrder || index;
            return qId !== questionId;
        });

        // Track backend IDs for deletion
        const questionToDelete = currentQuestions.find((q, index) => (q.id || q.displayOrder || index) === questionId);
        if (questionToDelete && typeof questionToDelete.id === 'number') {
            setDeletedQuestionIds(prev => [...prev, questionToDelete.id as number]);
        }

        // Remove from touched questions
        setTouchedQuestions(prev => {
            const newSet = new Set(prev);
            newSet.delete(questionId);
            return newSet;
        });

        // Update both local state and service
        setEvaluationQuestions(updatedQuestions);
        updateServiceEvaluation({
            evaluationQuestions: updatedQuestions
        });
    };

    const handleAddQuestion = () => {
        const currentQuestions = evaluationQuestions;
        const newQuestion: EvaluationQuestion = {
            questionText: '',
            enabled: true,
            required: true,
            questionableType: 'Evaluation',
            category: 'for_pets',
            displayOrder: currentQuestions.length + 1
        };

        const updatedQuestions = [...currentQuestions, newQuestion];

        // Update both local state and service
        setEvaluationQuestions(updatedQuestions);
        updateServiceEvaluation({
            evaluationQuestions: updatedQuestions
        });
    };

    // Handle category changes
    const handleCategoryChange = (categories: { grooming: boolean; daycare: boolean; boarding: boolean }) => {
        const selectedCategories = Object.entries(categories)
            .filter(([_, selected]) => selected)
            .map(([category, _]) => category);

        updateServiceEvaluation({
            category: selectedCategories
        });
    };

    // Handle evaluation expiry duration change
    const handleExpiryDurationChange = (duration: number) => {
        const durationString = duration.toString();
        updateEvaluationExpiry({
            duration: durationString
        });
    };

    // Handle evaluation expiry enabled change
    const handleExpiryEnabledChange = (enabled: boolean) => {
        updateEvaluationExpiry({
            enabled,
            duration: enabled ? '4' : ''
        });
    };

    // Get selected categories - simplified
    const selectedCategories = {
        grooming: localService.evaluation?.category?.includes('grooming') || false,
        daycare: localService.evaluation?.category?.includes('daycare') || false,
        boarding: localService.evaluation?.category?.includes('boarding') || false
    };

    return (
        <>
            {(loading && status !== ServicesStatus.Saving) || hasLoaded ? (
                <div className={styles.container}>
                    <div className={styles.loadingContainer}>
                        <Spinner size={33} />
                    </div>
                </div>
            ) : (
                <div className={styles.container}>
                    {/* Header Section */}
                    <EvaluationHeader
                        isEnabled={isEnabled}
                        onToggleEnabled={changeEnableHandler}
                    />

                    {/* Conditional Content Based on isEnabled */}
                    {isEnabled ? (
                        <>
                            <div className={styles.alertSection}>
                                <AlertMessage
                                    icon={faInfoCircle}
                                    bgColor="#F8F5F1"
                                    bdColor="#BAA997"
                                >
                                    Evaluation required—only approved pets can book select services
                                </AlertMessage>
                            </div>

                            {/* Main Content */}
                            <div className={styles.content}>
                                {/* Service Categories Section */}
                                <ServiceCategorySelector
                                    selectedCategories={selectedCategories}
                                    errors={errors}
                                    onUpdateCategories={handleCategoryChange}
                                />

                                {/* Basic Info Section */}
                                <EvaluationBasicInfo
                                    evaluationName={localService.name || ''}
                                    description={localService.description || ''}
                                    errors={errors}
                                    onUpdateName={name => setLocalService(prev => ({ ...prev, name }))}
                                    onUpdateDescription={description =>
                                        setLocalService(prev => ({ ...prev, description }))
                                    }
                                />

                                {/* Evaluation Expiry Section */}
                                <EvaluationToggles
                                    evaluationExpiry={localService.evaluation?.evaluationExpiry}
                                    errors={errors}
                                    onlineBookingEnabled={localService.olbEnabled || false}
                                    onUpdateExpiryEnabled={handleExpiryEnabledChange}
                                    onUpdateExpiryDuration={handleExpiryDurationChange}
                                    onUpdateOnlineBooking={(enabled: boolean) =>
                                        setLocalService(prev => ({ ...prev, olbEnabled: enabled }))
                                    }
                                />

                                {/* Price and Duration */}
                                <EvaluationPricing
                                    price={localService.pricing?.fixedPrice?.[0]?.price || 0}
                                    duration={
                                        (localService.duration as ServiceDuration) || {
                                            simple: { hours: 0, minutes: 15 },
                                            variable: []
                                        }
                                    }
                                    errors={errors}
                                    onUpdatePrice={(price: number) => {
                                        setLocalService(prev => ({
                                            ...prev,
                                            pricing: {
                                                ...prev.pricing,
                                                fixedPrice:
                                                    prev.pricing?.fixedPrice?.length > 0
                                                        ? prev.pricing.fixedPrice.map((fp, index) =>
                                                              index === 0 ? { ...fp, price } : fp
                                                          )
                                                        : [{ petType: prev.petTypes?.[0], price }]
                                            }
                                        }));
                                    }}
                                    onUpdateDuration={duration => {
                                        setLocalService(prev => ({
                                            ...prev,
                                            duration
                                        }));
                                    }}
                                />

                                {/* Taxable Service Section */}
                                <EvaluationTaxableService
                                    selectedTaxes={localService.pricing?.taxes || []}
                                    onUpdateTaxes={taxes =>
                                        setLocalService(prev => ({
                                            ...prev,
                                            pricing: {
                                                ...prev.pricing,
                                                taxes
                                            }
                                        }))
                                    }
                                />

                                {/* Staff Selector Section */}
                                <EvaluationStaffSelector
                                    selectedStaff={localService.staff || []}
                                    onUpdateStaff={staff =>
                                        setLocalService(prev => ({
                                            ...prev,
                                            staff
                                        }))
                                    }
                                />

                                {/* Color Code Section */}
                                <EvaluationColorSelector
                                    selectedColor={localService.color || null}
                                    onUpdateColor={color => setLocalService(prev => ({ ...prev, color }))}
                                    errors={errors}
                                />

                                {/* Custom Questions Section */}
                                <EvaluationQuestions
                                    evaluationQuestionsEnabled={
                                        localService.evaluation?.evaluationQuestionsEnabled || false
                                    }
                                    evaluationQuestions={evaluationQuestions}
                                    errors={errors}
                                    touchedQuestions={touchedQuestions}
                                    attemptedSave={attemptedSave}
                                    onToggleCustomQuestions={handleToggleCustomQuestions}
                                    onUpdateQuestion={handleUpdateQuestion}
                                    onRemoveQuestion={handleRemoveQuestion}
                                    onAddQuestion={handleAddQuestion}
                                />

                                {/* Save Button */}
                                <div className={styles.saveSection}>
                                    <Button
                                        className={styles.button}
                                        label="Save"
                                        variant="primary"
                                        onClick={handleSave}
                                        isLoading={loading}
                                        disabled={validateRequiredFields(localService)}
                                    />
                                </div>
                            </div>
                        </>
                    ) : (
                        /* Disabled State Message */
                        <div className={styles.disabledSection}>
                            <AlertMessage
                                icon={faInfoCircle}
                                bgColor="#F8F5F1"
                                bdColor="#BAA997"
                            >
                                Evaluation disabled—pets can book without an assessment.
                            </AlertMessage>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default EvaluationComponent;
